// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
//
// EXTRACTION PLAN -- required by the expert-legibility rule in
// `.claude/rules/anti-ai-slop.md`: this module was past the 400-line threshold.
//
// ALREADY EXTRACTED: the judgment receipts -> `./AskV2ReviewJudgmentReceipt.tsx`. The tag and
// source filter -> `./AskV2ReviewTagFilter.tsx`. Which section a finding sits
// in -> `reviewCommentGroup` in `../../utils/visibleReviewComments.ts`. The
// section heading, toggle and list -> `./AskV2ReviewCommentSection.tsx`. One
// card -> `./AskV2ReviewCommentCard.tsx`; card behaviour belongs there, not here.
// The moved-to-a-section snackbar -> `./useReviewCommentMoveNotice.ts`.
import {
  Fragment,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
  AskV2ReviewCommentCard,
  type AskV2ReviewCommentEditHandler,
} from "./AskV2ReviewCommentCard";
import { AskV2ReviewCommentSection } from "./AskV2ReviewCommentSection";
import { AskV2ReviewGroupSection } from "./AskV2ReviewGroupSection";
import { AskV2ReviewSeverityBands } from "./AskV2ReviewSeverityBands";
import {
  AskV2ReviewTagFilter,
  type ReviewSourceFilterOption,
} from "./AskV2ReviewTagFilter";
import { groupMark, isMinorFinding, type PinGroup } from "./pinGroups";
import { type RejectionScope } from "./rememberedSentence";
import {
  defaultActiveReviewCommentTags,
  REVIEW_COMMENT_TAG_IDS,
  type CommentWithOptionalTags,
  type ReviewCommentSourceKey,
  type ReviewCommentTagId,
} from "./reviewCommentTags";
import { useReviewCommentMoveNotice } from "./useReviewCommentMoveNotice";

import { type AssetFile } from "../../components/ask-puntt-review";
import {
  claimCitationsFor,
  placedCommentMemoryIds,
} from "../../components/ask-puntt-review/claimCitations";
import { type LinkableClaim } from "../../plugins/rehype-claim-citation";
import {
  type LinkableMemory,
  memoryLinksFor,
} from "../../utils/memoryCitationFields";
import {
  REVIEW_COMMENT_GROUPS,
  type ReviewCommentGroup,
  reviewCommentGroup,
} from "../../utils/visibleReviewComments";

const SECTION_LABEL_KEYS = {
  unresolved: "askV2.review.sectionUnresolved",
  confirmed: "askV2.review.sectionConfirmed",
  rejected: "askV2.review.sectionRejected",
} as const;

type AskV2ReviewCommentsListProps = {
  comments: readonly CommentWithOptionalTags[];
  /** A finding's typography companions, drawn inside its card (`pinGroups.attachCompanions`). */
  companionsOf?: ReadonlyMap<string, readonly CommentWithOptionalTags[]>;
  commentNumbers?: Readonly<Record<string, number>>;
  contextFiles?: readonly AssetFile[];
  reviewFiles?: readonly AssetFile[];
  selectedCommentId?: string | null;
  authorNameByUserId?: Readonly<Record<string, string>>;
  currentUserId?: string;
  currentUserName?: string;
  activeTags?: ReadonlySet<ReviewCommentTagId>;
  alwaysOn?: boolean;
  showTagFilter?: boolean;
  onToggleTag?: (tag: ReviewCommentTagId, checked: boolean) => void;
  onToggleAlwaysOn?: (checked: boolean) => void;
  /**
   * Comments before the filter. The section heading counts how many of these
   * the filter hid. Absent, nothing is reported hidden.
   */
  allComments?: readonly CommentWithOptionalTags[];
  /** The "raised by" half of the filter; absent when no expert raised a finding. */
  sourceFilter?: {
    sources: readonly ReviewSourceFilterOption[];
    hidden: ReadonlySet<ReviewCommentSourceKey>;
    onToggle: (source: ReviewCommentSourceKey, checked: boolean) => void;
  };
  judgmentPending?: boolean;
  onJudge?: (
    commentId: string,
    liked: boolean | null,
    scope?: RejectionScope,
  ) => void;
  /** Saves the caller's edit of their own comment; absent, no card offers Edit. */
  onEdit?: AskV2ReviewCommentEditHandler;
  onSelect: (commentId: string) => void;
  onClear: () => void;
  /** Drawn right after the Unresolved section: what an earlier version raised and this one fixed. */
  afterUnresolved?: ReactNode;
  /** The open findings in groups (spec dave.savostyanov/0090); absent, Unresolved lists them flat. */
  pinGroups?: {
    groups: readonly PinGroup[];
    openGroupKey: string | null;
    onToggleGroup: (key: string) => void;
  };
  /** Drawn in the header beside the type filter: the pin view controls. */
  headerControls?: ReactNode;
  /** "All pins" view: Unresolved opens with the findings to fix, the minor and check ones in a band that folds. */
  severityBands?: boolean;
};

export function AskV2ReviewCommentsList({
  comments,
  companionsOf,
  commentNumbers: commentNumbersProp,
  contextFiles = [],
  reviewFiles = [],
  selectedCommentId: selectedProp = null,
  authorNameByUserId,
  currentUserId,
  currentUserName,
  activeTags = defaultActiveReviewCommentTags(),
  alwaysOn = true,
  showTagFilter = false,
  onToggleTag = () => undefined,
  onToggleAlwaysOn = () => undefined,
  allComments,
  sourceFilter,
  judgmentPending = false,
  onJudge,
  onEdit,
  onSelect,
  onClear,
  afterUnresolved,
  pinGroups,
  headerControls,
  severityBands = false,
}: AskV2ReviewCommentsListProps) {
  const { t } = useTranslation("ui");
  const sectionIdBase = useId();
  // A companion has no card of its own: its lead's card is the one selected.
  const leadOfCompanion = useMemo(() => {
    const byCompanion = new Map<string, string>();
    companionsOf?.forEach((list, lead) =>
      list.forEach((c) => byCompanion.set(c._id, lead)),
    );
    return byCompanion;
  }, [companionsOf]);
  const selectedCommentId =
    selectedProp == null
      ? null
      : (leadOfCompanion.get(selectedProp) ?? selectedProp);
  const commentNumbers = useMemo(() => {
    if (commentNumbersProp) return commentNumbersProp;
    const numbers: Record<string, number> = {};
    let index = 0;
    for (const comment of comments) {
      if (comment.isDeleted) continue;
      index += 1;
      numbers[comment._id] = index;
    }
    return numbers;
  }, [commentNumbersProp, comments]);

  // The saved memories each comment rests on, numbered over the comment's own
  // text. Derived once per list, and before the claims: a memory whose sentence
  // takes a chip must not also link the claim wording it hangs on.
  const memoryCitationsByComment = useMemo(() => {
    const byId = new Map<string, LinkableMemory[]>();
    for (const comment of comments) {
      byId.set(
        comment._id,
        memoryLinksFor(comment.citations, comment.description, comment._id),
      );
    }
    return byId;
  }, [comments]);

  // Derived once per comment list, not inside the render map. Called inline it
  // handed `AskPunttReviewCommentBody` a fresh array identity on every render
  // of this list -- and that array is what `Markdown` keys its rehype-plugin
  // memo on, so every unrelated re-render (a tag filter, a selection) rebuilt
  // the unified processor for every comment on screen. `ChatMessage` already
  // memoises the same derivation; this is the list doing the same.
  const claimCitationsByComment = useMemo(() => {
    const byId = new Map<string, LinkableClaim[]>();
    for (const comment of comments) {
      const placed = placedCommentMemoryIds(
        comment.description,
        memoryCitationsByComment.get(comment._id) ?? [],
      );
      byId.set(comment._id, claimCitationsFor(comment, undefined, placed));
    }
    return byId;
  }, [comments, memoryCitationsByComment]);

  const authorOptions = {
    aiLabel: t("aiReviewer"),
    currentUserId,
    currentUserName,
    authorNameByUserId,
    unknownAuthorLabel: t("member"),
  };

  // Why the list is empty, which is not always "you filtered it". A review
  // that found nothing -- or one whose checks were all blocked -- used to read
  // "No findings match the selected types" while every type was still
  // selected, and with `showTagFilter` false the control that copy blames is
  // not even on screen. A rejection never empties it: rejected findings stay
  // in the list, grayed.
  const tagFilterNarrows =
    !alwaysOn ||
    REVIEW_COMMENT_TAG_IDS.some((tag) => !activeTags.has(tag)) ||
    (sourceFilter?.hidden.size ?? 0) > 0;
  const emptyStateTestId = tagFilterNarrows
    ? "ask-v2-review-tag-filter-empty"
    : "ask-v2-review-no-findings-empty";
  const emptyStateCopy = tagFilterNarrows
    ? t("askV2.review.tagFilterEmpty")
    : t("askV2.review.noFindings");

  // Findings are grouped by what has been decided on them now: Unresolved
  // holds every finding nobody has approved or rejected, new in this review or
  // raised in a past one. A judgment made here files the card under its new
  // section at once, so the counts and the buckets always agree with the
  // server's copy the judgment patched in. All three always render, empty ones
  // included, so "Approved 0" says there is nothing approved yet rather than
  // leaving her to wonder where it went.
  useReviewCommentMoveNotice(comments);
  const grouped = useMemo(() => {
    const byGroup: Record<ReviewCommentGroup, CommentWithOptionalTags[]> = {
      unresolved: [],
      confirmed: [],
      rejected: [],
    };
    for (const comment of comments) {
      byGroup[reviewCommentGroup(comment)].push(comment);
    }
    return byGroup;
  }, [comments]);
  const sections = useMemo(
    () =>
      REVIEW_COMMENT_GROUPS.map((key) => ({
        key,
        labelKey: SECTION_LABEL_KEYS[key],
        items: grouped[key],
      })),
    [grouped],
  );
  // How many of each section's findings the filter hides, for its heading.
  const sectionSizes = useMemo(() => {
    const counts: Record<ReviewCommentGroup, number> = {
      unresolved: 0,
      confirmed: 0,
      rejected: 0,
    };
    for (const comment of allComments ?? comments) {
      if (comment.isDeleted) continue;
      counts[reviewCommentGroup(comment)] += 1;
    }
    return counts;
  }, [allComments, comments]);
  const unresolvedCount = grouped.unresolved.length;
  const showSections = comments.length > 0;
  // Only Unresolved starts open: it is the list she works through. Approved
  // and Rejected start collapsed (Surbhi, 2026-10-07), their counts still on
  // the heading, so a decided finding is one click away rather than gone.
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    () => new Set(["confirmed", "rejected"]),
  );
  // why: `sections` takes a new identity on every poll, filter change or added
  // comment; opening on that would reopen a section she closed while its
  // finding is still selected. Open only when the selection moves onto a
  // finding.
  const [revealMemory] = useState(() => {
    let revealed: string | null = null;
    return {
      get: () => revealed,
      set: (next: string | null) => {
        revealed = next;
      },
    };
  });
  const isSectionOpen = (key: string) => !collapsedSections.has(key);
  const [minorBandOpen, setMinorBandOpen] = useState(true);
  const bandsOn = severityBands && !pinGroups;
  // why: a minor finding picked on the artwork must not land in the folded band, out of sight.
  const selectedInFoldedBand =
    bandsOn &&
    !minorBandOpen &&
    grouped.unresolved.some(
      (c) => c._id === selectedCommentId && isMinorFinding(c),
    );
  useEffect(() => {
    if (selectedInFoldedBand) setMinorBandOpen(true);
  }, [selectedInFoldedBand]);
  // why: a card she approves or rejects leaves its section for one that may
  // be collapsed; open that one so the card she just judged -- and its
  // Restore -- stays in sight (#5927). Only a move opens a section: a finding
  // a running review adds is not one, nor is a remount.
  const lastGroups = useRef<Map<string, ReviewCommentGroup> | null>(null);
  useEffect(() => {
    const before = lastGroups.current;
    const now = new Map<string, ReviewCommentGroup>();
    const movedInto: ReviewCommentGroup[] = [];
    for (const group of REVIEW_COMMENT_GROUPS) {
      for (const comment of grouped[group]) {
        now.set(comment._id, group);
        const was = before?.get(comment._id);
        if (was != null && was !== group && !movedInto.includes(group)) {
          movedInto.push(group);
        }
      }
    }
    lastGroups.current = now;
    setCollapsedSections((prev) => {
      // Stryker disable next-line ConditionalExpression: equivalent -- without the bail-out the updater returns a copy of prev with the same keys, so only render identity changes
      if (!movedInto.some((key) => prev.has(key))) return prev;
      const next = new Set(prev);
      movedInto.forEach((key) => next.delete(key));
      return next;
    });
  }, [grouped]);
  // why: selecting a finding from the artwork (its pin) must never land on a
  // card hidden inside a closed section -- open the section that holds it.
  useEffect(() => {
    if (!selectedCommentId) {
      revealMemory.set(null);
      return;
    }
    const holder = sections.find((section) =>
      section.items.some((comment) => comment._id === selectedCommentId),
    );
    if (!holder) return;
    if (revealMemory.get() === selectedCommentId) return;
    revealMemory.set(selectedCommentId);
    setCollapsedSections((prev) => {
      if (!prev.has(holder.key)) return prev;
      const next = new Set(prev);
      next.delete(holder.key);
      return next;
    });
  }, [selectedCommentId, sections, revealMemory]);
  // why: a finding selected out of a closed section renders only once the
  // effect above opens it, so scroll when its card can be found -- not only
  // when the selection moves, or it is left off-screen.
  const selectedShown =
    selectedCommentId != null &&
    !selectedInFoldedBand &&
    (!showSections ||
      sections.some(
        (section) =>
          isSectionOpen(section.key) &&
          section.items.some((comment) => comment._id === selectedCommentId),
      ));
  useLayoutEffect(() => {
    if (!selectedCommentId || !selectedShown) return;
    document
      .querySelector(
        `[data-testid="ask-v2-review-comment-card-${selectedCommentId}"]`,
      )
      ?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [selectedCommentId, selectedShown]);
  // The finding whose header delete opened the scope prompt. The prompt
  // renders in that card's open body, so delete on a closed card opens it.
  const [rejectingCommentId, setRejectingCommentId] = useState<string | null>(
    null,
  );
  // why: artwork selection never hits the delete handler; clearing when the selection IS this finding would hide the prompt a closed-card delete just opened
  useEffect(() => {
    setRejectingCommentId((current) =>
      current != null &&
      current !== selectedCommentId &&
      leadOfCompanion.get(current) !== selectedCommentId
        ? null
        : current,
    );
  }, [leadOfCompanion, selectedCommentId]);
  const toggleSection = (key: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderComment = (comment: CommentWithOptionalTags) => (
    <AskV2ReviewCommentCard
      key={comment._id}
      comment={comment}
      companions={companionsOf?.get(comment._id)}
      rejectingId={rejectingCommentId}
      commentNumber={commentNumbers[comment._id] ?? 0}
      expanded={selectedCommentId === comment._id}
      rejecting={rejectingCommentId === comment._id}
      authorOptions={authorOptions}
      claimCitations={claimCitationsByComment.get(comment._id)}
      memoryCitations={memoryCitationsByComment.get(comment._id)}
      contextFiles={contextFiles}
      reviewFiles={reviewFiles}
      authorNameByUserId={authorNameByUserId}
      currentUserId={currentUserId}
      currentUserName={currentUserName}
      judgmentPending={judgmentPending}
      onJudge={onJudge}
      onEdit={onEdit}
      onSelect={onSelect}
      onClear={onClear}
      onRejectingChange={setRejectingCommentId}
    />
  );

  return (
    <section
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      data-testid="ask-v2-review-comments"
      aria-label={t("askV2.review.findingsLabel")}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        {/* why: the split sits on the title's line, so a re-review's header is
            as tall as a first review's and the cards stay level with the
            artwork pane beside it. */}
        <div
          className="flex min-w-0 items-baseline gap-2"
          data-testid="ask-v2-review-findings-title-row"
        >
          <h2
            className="shrink-0 text-[13px] font-bold text-puntt-neutral-gray-12"
            data-testid="ask-v2-review-findings-title"
          >
            {t("askV2.review.findingsTitle")}
          </h2>
          {showSections ? (
            // why: the header names what is still open, so a review's total
            // never reads as that many findings left to act on.
            <p
              className="m-0 truncate text-[12px] text-puntt-neutral-gray-10"
              data-testid="ask-v2-review-findings-split"
            >
              {t("askV2.review.findingsSplitUnresolved", {
                count: unresolvedCount,
              })}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {headerControls}
          {showTagFilter ? (
            <AskV2ReviewTagFilter
              activeTags={activeTags}
              alwaysOn={alwaysOn}
              onToggle={onToggleTag}
              onToggleAlwaysOn={onToggleAlwaysOn}
              sources={sourceFilter?.sources}
              hiddenSources={sourceFilter?.hidden}
              onToggleSource={sourceFilter?.onToggle}
            />
          ) : null}
        </div>
      </div>
      <ul
        className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
        data-testid="ask-v2-review-comment-list"
        aria-label={t("askV2.review.commentsLabel")}
        // why: a memory chip's card keeps inside this list, scrolling yet or not, so a short list never lets it slide over the artwork pane (`popoverBoundary`).
        data-popover-boundary
      >
        {comments.length === 0 ? (
          <li
            className="px-2 py-6 text-center text-[13px] text-puntt-neutral-gray-10"
            data-testid={emptyStateTestId}
          >
            {emptyStateCopy}
          </li>
        ) : null}
        {showSections
          ? sections.map((section) => (
              <Fragment key={section.key}>
                <AskV2ReviewCommentSection
                  sectionKey={section.key}
                  label={t(section.labelKey)}
                  count={section.items.length}
                  hiddenCount={Math.max(
                    0,
                    sectionSizes[section.key] - section.items.length,
                  )}
                  open={isSectionOpen(section.key)}
                  listId={`${sectionIdBase}-${section.key}`}
                  onToggle={() => toggleSection(section.key)}
                >
                  {!isSectionOpen(section.key) ? null : section.key ===
                      "unresolved" && pinGroups ? (
                    pinGroups.groups.map((group, index) => (
                      <AskV2ReviewGroupSection
                        key={group.key}
                        group={group}
                        mark={groupMark(index)}
                        open={pinGroups.openGroupKey === group.key}
                        onToggle={() => pinGroups.onToggleGroup(group.key)}
                      >
                        {group.members.map(renderComment)}
                      </AskV2ReviewGroupSection>
                    ))
                  ) : section.key === "unresolved" && bandsOn ? (
                    <AskV2ReviewSeverityBands
                      items={section.items}
                      minorOpen={minorBandOpen}
                      onToggleMinor={() => setMinorBandOpen((open) => !open)}
                      renderComment={renderComment}
                    />
                  ) : (
                    section.items.map(renderComment)
                  )}
                </AskV2ReviewCommentSection>
                {section.key === "unresolved" ? afterUnresolved : null}
              </Fragment>
            ))
          : comments.map(renderComment)}
        {showSections ? null : afterUnresolved}
      </ul>
    </section>
  );
}
